// Swap-Bewertung beim Import (Kostenbasis + Anzeige).
//
// FIFO bewertet einen Swap über `tx.preis` (fifo.js: effectivePreis = D(tx.preis)).
// Fehlt der Preis (z.B. weil der serverseitige Abruf in solana-wallet-sync
// scheiterte), wäre die Kostenbasis 0 / costBasisUnknown und die Anzeige 0 €.
//
// Dieser client-seitige Schritt füllt jeden Swap mit `preis=0` aus der BEKANNTEN
// (nicht-Fiat/Stablecoin) Seite × historischem CoinGecko-Tagespreis — robust über
// den gecachten getHistoricalPriceAt (mit CryptoCompare-Fallback). Mutiert
// `tx.preis` in-place; wird VOR Speichern/Review ausgeführt, damit der Wert
// persistiert wird (Kostenbasis stimmt) und in der Vorschau erscheint.
//
// Eiserne Regel: NUR Swaps (Empfangsseite = Erwerb), NIE interne Transfers.
// Echter preis (>0) und Fiat/Stablecoin-Seiten bleiben unangetastet (FIFO/
// resolveSwapEurValue decken die exakt ab).

import { getHistoricalPriceAt, COIN_MAP } from './coingecko';
import { isStablecoin } from './stablecoinResolver';
import { INCOME_TYPES } from './transactionTypes';

const FIAT = new Set(['EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL']);

const knownNonFiat = (sym, menge) => {
  const up = String(sym || '').toUpperCase();
  if (!up || FIAT.has(up) || isStablecoin(up)) return null;
  const id = COIN_MAP[up]?.id;
  const m = Number(menge);
  return (id && m > 0) ? { id, sym: up, menge: m } : null;
};

// Bestimmt die zu bepreisende Seite eines Swaps: bekannter (COIN_MAP) Coin, der
// KEIN Fiat/Stablecoin ist. OUT bevorzugt (abgegebene Seite = Wert des Tauschs).
export function swapPriceSide(tx) {
  if (!tx || tx.typ !== 'swap') return null;
  return knownNonFiat(tx.coinOut, tx.mengeOut) || knownNonFiat(tx.coinIn, tx.mengeIn);
}

// Welche Seite/Menge bepreist eine TX bei fehlendem preis?
//  - swap          → bekannte Seite (s.o.)
//  - income        → Coin × Menge = Anschaffungswert (korrekte Kostenbasis)
//  - einzahlung    → matched: preis von FIFO ignoriert (Basis wandert mit);
//                    unmatched: = externer-Empfang-Kostenbasis (gewollt)
//  - auszahlung    → reiner Anzeige-Gegenwert (gv immer null, keine Kostenbasis)
//  - gebuehr       → Coin-Gebühr-Gegenwert
//  - kauf/verkauf  → Coin × historischer Tagespreis (Kostenbasis bzw. Erlös). NUR bei
//                    preis=0 (echter Preis aus dem CSV gewinnt immer). Marktwert ist
//                    weit besser als 0 € (sonst Phantom-Gewinn/„Basis unvollständig").
//                    User-Direktive 2026-06-24: „wenn wir den Preis kennen, setzen wir
//                    ihn — automatisch, keine Knöpfe." Fiat-Coin → NICHT (knownNonFiat).
const PRICED_SINGLE = new Set(['einzahlung', 'auszahlung', 'gebuehr', 'kauf', 'verkauf']);
export function pricingSide(tx) {
  if (!tx) return null;
  if (tx.typ === 'swap') return swapPriceSide(tx);
  if (PRICED_SINGLE.has(tx.typ) || INCOME_TYPES.has(tx.typ)) {
    return knownNonFiat(tx.coin || tx.sym, tx.menge);
  }
  return null;
}

/**
 * Setzt `preis` für bewertbare TX mit preis=0 (in-place) aus historischem
 * Tagespreis. Async, best-effort. Echter preis bleibt; Kauf/Verkauf/Fiat außen vor.
 * @param {Array} txs
 * @param {Object} opts
 * @param {string} [opts.currency='eur']
 * @param {Function} [opts.fetchPrice]  (id, datum, currency, sym) => Promise<number|null>
 * @returns {Promise<number>} Anzahl bewerteter TX
 */
export async function enrichMissingPrices(txs, { currency = 'eur', fetchPrice = getHistoricalPriceAt } = {}) {
  let enriched = 0;
  for (const tx of txs || []) {
    if (!tx) continue;
    if (Number(tx.preis) > 0) continue;            // echter Preis gewinnt
    if (!tx.datum || typeof tx.datum !== 'string') continue;
    const side = pricingSide(tx);
    if (!side) continue;
    try {
      const unit = await fetchPrice(side.id, tx.datum.slice(0, 10), currency, side.sym);
      if (typeof unit === 'number' && unit > 0) {
        tx.preis = side.menge * unit;
        tx._priceDerived = true;                   // Audit-Marker (abgeleitet)
        enriched++;
      }
    } catch {
      /* einzelner Miss ignorieren */
    }
  }
  return enriched;
}

// Rückwärtskompatibler Alias (nur Swaps werden weiterhin korrekt behandelt).
export const enrichSwapPrices = enrichMissingPrices;
